// preFigure — what a locked tax surface says BEFORE the engine has produced a
// figure for this user.
//
// A price is a question about a number the user has not seen. Asking it while
// the first import or the first calculation is still running is an interruption
// with nothing behind it: users 421, 442 and 410 were each shown a paywall
// within two minutes of signing up, before any exchange had finished syncing.
// So until `hasFigure` (read from `operation_status`, never inferred from row
// counts — docs/design/ui-state-invariants.md I1) the same trigger points show
// the job instead, and the CTA opens it (I6 — every live indicator is a door).
//
// Returns null once a figure exists; the caller then shows its paywall as before.

import { usePipelineState } from "@/hooks/usePipelineState";

export interface PreFigureState {
  eyebrow: string;
  heading: string;
  body: string;
  ctaLabel: string;
  ctaTo: string;
}

export const usePreFigureState = (): PreFigureState | null => {
  const { busy, rowLabel, hasFigure } = usePipelineState();

  if (hasFigure) return null;

  if (busy) {
    return {
      eyebrow: rowLabel ?? "Working",
      heading: "Your figures are on their way",
      body: "We'll have your capital gains and income as soon as this finishes. Nothing to decide until then.",
      ctaLabel: "View progress",
      ctaTo: "/sync-status",
    };
  }

  return {
    eyebrow: "Nothing calculated yet",
    heading: "Connect a source to see your figures",
    body: "Link an exchange or wallet and we'll work out your capital gains and income. Your headline figure is free.",
    ctaLabel: "Connect a source",
    ctaTo: "/integrations",
  };
};
